// READ-ONLY diagnostic for the text-canonicalization workstream (Phase 1).
//
// It does NOT change any comparison, display, or stored value. It answers, over the real merged
// dataset, whether swapping the name/description drift comparison from the current normalization to the
// new comparisonKey is safe and worthwhile:
//
//   1. accepted row count
//   2. accepted rows preserved (unchanged acceptance)
//   3. compatibility violations  -- accepted rows that would NEWLY drift (must be zero)
//   4. false-positive drift resolved -- rows drifting today only on case/whitespace
//   5. collisions introduced by each added fold, split into EXPECTED vs REVIEW
//
// Only the fields whose drift RESETS acceptance are considered (facilityName rename is context-only and
// excluded). Of those, only the NAME/description comparison changes (trim -> comparisonKey); ids, dates
// and facilityId keep their exact current comparators, so they can neither cause a violation nor a
// resolution -- they are included so row-level drift matches the real merge.

import {
  comparisonKey,
  canonicalizeId,
  canonicalizeText,
  legacyNormalizeId,
  legacyTrim,
  STANDARDIZE_PROFILE,
} from "./textCanonicalization";

// Fields the merge uses to reset `accepted`, with their OLD (current) and NEW comparators.
// NAME fields are the only ones whose comparator changes.
const ACCEPTANCE_FIELDS = [
  { field: "seasonName", kind: "name", oldFn: legacyTrim, newFn: comparisonKey },
  { field: "seasonDesc", kind: "name", oldFn: legacyTrim, newFn: comparisonKey },
  { field: "priorYearSeasonName", kind: "name", oldFn: legacyTrim, newFn: comparisonKey },
  { field: "priorSeasonName", kind: "name", oldFn: legacyTrim, newFn: comparisonKey },
  { field: "facilityId", kind: "unchanged", oldFn: legacyTrim, newFn: legacyTrim },
  { field: "seasonDateS", kind: "unchanged", oldFn: legacyTrim, newFn: legacyTrim },
  { field: "seasonDateE", kind: "unchanged", oldFn: legacyTrim, newFn: legacyTrim },
  { field: "seasonID", kind: "id", oldFn: legacyNormalizeId, newFn: canonicalizeId },
  { field: "priorYearSeasonId", kind: "id", oldFn: legacyNormalizeId, newFn: canonicalizeId },
  { field: "priorSeasonId", kind: "id", oldFn: legacyNormalizeId, newFn: canonicalizeId },
];

const isAccepted = (row) => row.accepted === true || row.accepted === 1 || row.accepted === "TRUE";

// Per-field drift under old vs new, for a matched (saved, warehouse) pair.
function fieldDiffs(savedRow, warehouseRow) {
  const oldDrift = [];
  const newDrift = [];
  for (const { field, oldFn, newFn } of ACCEPTANCE_FIELDS) {
    if (oldFn(warehouseRow[field]) !== oldFn(savedRow[field])) oldDrift.push(field);
    if (newFn(warehouseRow[field]) !== newFn(savedRow[field])) newDrift.push(field);
  }
  return { oldDrift, newDrift };
}

// Main acceptance-preservation diagnostic. `baseData` = saved rows, `newData` = warehouse-current rows,
// exactly as SeasonAudit's mergeLists receives them.
export function runAcceptanceDiagnostic(baseData = [], newData = []) {
  const warehouseBySkey = new Map(newData.map((r) => [r.sKey, r]));

  const accepted = baseData.filter(isAccepted);
  const comparable = accepted.filter((r) => warehouseBySkey.has(r.sKey)); // only these are drift-checked

  const violations = []; // accepted, was agreeing, would NEWLY drift  (expected: none)
  const resolved = [];   // was drifting under old, agrees under new    (the win)
  let preserved = 0;     // accepted rows whose acceptance is not newly threatened

  for (const savedRow of comparable) {
    const warehouseRow = warehouseBySkey.get(savedRow.sKey);
    const { oldDrift, newDrift } = fieldDiffs(savedRow, warehouseRow);
    const oldDrifted = oldDrift.length > 0;
    const newDrifted = newDrift.length > 0;

    if (!oldDrifted && newDrifted) {
      violations.push({ sKey: savedRow.sKey, fields: newDrift,
        detail: newDrift.map((f) => ({ field: f, saved: savedRow[f], warehouse: warehouseRow[f] })) });
    }
    if (oldDrifted && !newDrifted) {
      resolved.push({ sKey: savedRow.sKey, fields: oldDrift,
        detail: oldDrift.map((f) => ({ field: f, saved: savedRow[f], warehouse: warehouseRow[f] })) });
    }
    if (!newDrifted) preserved += 1; // acceptance not threatened by the change
  }

  // False-positive resolution across ALL comparable rows (not only accepted), for completeness.
  let falsePositiveDriftResolvedAllRows = 0;
  for (const warehouseRow of newData) {
    const savedRow = baseData.find((r) => r.sKey === warehouseRow.sKey);
    if (!savedRow) continue;
    const { oldDrift, newDrift } = fieldDiffs(savedRow, warehouseRow);
    if (oldDrift.length > 0 && newDrift.length === 0) falsePositiveDriftResolvedAllRows += 1;
  }

  return {
    acceptedRowCount: accepted.length,
    comparableAcceptedRowCount: comparable.length,
    acceptedRowsPreserved: preserved,
    compatibilityViolations: violations,        // MUST be empty
    falsePositiveDriftResolvedAccepted: resolved,
    falsePositiveDriftResolvedAllRows,
  };
}

// ---- collision reporting --------------------------------------------------------------------------

// Two DISTINCT source values that share a key: if even the cosmetic-only STANDARDIZE profile equates
// them, the difference was purely case / Unicode / whitespace -> EXPECTED/desirable. Otherwise the key
// merged them via punctuation-stripping or an alias -> REVIEW (may hide a meaningful difference).
export function classifyCollision(a, b) {
  return canonicalizeText(a, STANDARDIZE_PROFILE) === canonicalizeText(b, STANDARDIZE_PROFILE)
    ? "expected"
    : "review";
}

// Group raw values by a key function; return groups holding >1 DISTINCT source value, each classified.
function collisionsForKey(rawValues, keyFn) {
  const groups = new Map();
  for (const raw of rawValues) {
    const key = keyFn(raw);
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(raw);
  }
  const collisions = [];
  for (const [key, sources] of groups) {
    if (sources.size < 2) continue;
    const members = [...sources];
    // A group is "review" if ANY pair in it is review-worthy (differs beyond case/Unicode/whitespace).
    let verdict = "expected";
    for (let i = 0; i < members.length && verdict === "expected"; i++) {
      for (let j = i + 1; j < members.length; j++) {
        if (classifyCollision(members[i], members[j]) === "review") { verdict = "review"; break; }
      }
    }
    collisions.push({ key, members, verdict });
  }
  return collisions;
}

// Incremental collision report: for an ordered list of progressively-coarser key stages, report the
// collisions each stage NEWLY introduces vs the previous stage, split into expected vs review.
export function incrementalCollisionReport(rawValues, stages) {
  const uniqueRaw = [...new Set(rawValues.filter((v) => v !== null && v !== undefined && String(v).trim() !== ""))];
  const report = [];
  let previousKeyOf = null;
  for (const stage of stages) {
    const collisions = collisionsForKey(uniqueRaw, stage.keyFn);
    // "Newly introduced" = groups that were NOT already fully merged at the previous stage.
    const newlyIntroduced = collisions.filter((group) => {
      if (!previousKeyOf) return true; // baseline: every collision is "introduced by" the baseline
      const priorKeys = new Set(group.members.map((m) => previousKeyOf(m)));
      return priorKeys.size > 1; // members were split before this stage, merged now
    });
    report.push({
      stage: stage.name,
      introduced: newlyIntroduced.length,
      expected: newlyIntroduced.filter((g) => g.verdict === "expected").length,
      review: newlyIntroduced.filter((g) => g.verdict === "review").length,
      reviewExamples: newlyIntroduced.filter((g) => g.verdict === "review").slice(0, 25),
    });
    previousKeyOf = stage.keyFn;
  }
  return report;
}

// Collect the name/description field values across the merged dataset -- the corpus the folds act on.
export function collectNameValues(baseData = [], newData = []) {
  const fields = ["seasonName", "seasonDesc", "priorYearSeasonName", "priorSeasonName"];
  const values = [];
  for (const row of [...baseData, ...newData]) {
    for (const f of fields) values.push(row[f]);
  }
  return values;
}

// Convenience: run the full Phase-1 diagnostic and return a single report object.
export function runFullDiagnostic(baseData = [], newData = []) {
  const acceptance = runAcceptanceDiagnostic(baseData, newData);
  const nameValues = collectNameValues(baseData, newData);
  // Stages: legacy strip (baseline) -> + case fold -> + whitespace collapse (= active comparisonKey).
  const collisions = incrementalCollisionReport(nameValues, [
    { name: "legacy strip (baseline)", keyFn: (v) => canonicalizeText(v, { nfkc: false, standardizePunctuation: false, stripPunctuation: true, foldAccents: false, foldCase: false, collapseWhitespace: false }).trimEnd() },
    { name: "+ case fold", keyFn: (v) => canonicalizeText(v, { nfkc: false, standardizePunctuation: false, stripPunctuation: true, foldAccents: false, foldCase: true, collapseWhitespace: false }).trimEnd() },
    { name: "+ whitespace collapse (active key)", keyFn: (v) => comparisonKey(v) },
  ]);
  return { acceptance, collisions };
}
